import type { TGooglePlaceSuggestion } from "@/api/google-maps/search/google-search.type.ts";
import type { IAddressAdapter } from "@/domains/address/adapters/address-adapter.ts";
import type { TGoogleNormalizedAddress } from "@/domains/address/dto/normalized-address.ts";
import type { GoogleAddress } from "@/domains/address/providers/google/google-address.ts";

import { EAddressProvider } from "@/domains/address/providers/address-provider.types.ts";
import { EGoogleAddressType } from "@/lib/google.ts";

function extractPostalCode(suggestion: TGooglePlaceSuggestion) {
  const postalCode = suggestion.addressComponents.find((k) =>
    k.types.includes(EGoogleAddressType.POSTAL_CODE),
  )?.longText;

  if (postalCode) {
    return postalCode;
  }

  return (
    suggestion.addressComponents.find((k) => k.types.includes(EGoogleAddressType.POSTAL_CODE_PREFIX))?.longText ?? "/"
  );
}

function extractStreetNumber(suggestion: TGooglePlaceSuggestion) {
  return suggestion.addressComponents.find((k) => k.types.includes(EGoogleAddressType.STREET_NUMBER))?.longText ?? null;
}

function extractPremise(suggestion: TGooglePlaceSuggestion) {
  return suggestion.addressComponents.find((k) => k.types.includes(EGoogleAddressType.PREMISE))?.longText ?? null;
}

function extractRoute(suggestion: TGooglePlaceSuggestion) {
  return suggestion.addressComponents.find((k) => k.types.includes(EGoogleAddressType.ROUTE))?.longText ?? null;
}

function extractStreet(suggestion: TGooglePlaceSuggestion) {
  return extractRoute(suggestion) ?? extractPremise(suggestion) ?? "/";
}

function extractCity(suggestion: TGooglePlaceSuggestion) {
  return extractCityFromAddressComponents(suggestion.addressComponents) ?? "/";
}

function extractCityFromAddressComponents(addressComponents: TGooglePlaceSuggestion["addressComponents"]) {
  return addressComponents.find(
    (k) => k.types.includes(EGoogleAddressType.LOCALITY) || k.types.includes(EGoogleAddressType.POSTAL_TOWN),
  )?.longText;
}

export const GoogleAddressToNormalizeAddressAdapter: IAddressAdapter<GoogleAddress, TGoogleNormalizedAddress> = {
  adapt(googleAddress: GoogleAddress): TGoogleNormalizedAddress {
    const rawAddress = googleAddress.getRaw();
    return {
      raw: rawAddress,
      data: {
        streetNumber: extractStreetNumber(rawAddress),
        street: extractStreet(rawAddress),
        postalCode: extractPostalCode(rawAddress),
        cityCode: null, // Google Places API does not provide city codes
        city: extractCity(rawAddress),
      },
      provider: EAddressProvider.GOOGLE,
    };
  },
};
